import { z } from 'zod';
import { ReadinessSchema } from './intake.js';
import { ConsentRecordSchema, NotificationPreferencesSchema } from './notifications.js';
/**
 * The patient portal **API** contract (M10.1, `API.md` §2.4, FR-PORTAL-02..06).
 *
 * Everything a signed-in patient may see about their own record, and the three things
 * they may change without phoning: their contact details, the time of a visit, and
 * whether a visit happens at all.
 *
 * ## Three rules the whole surface inherits
 *
 * 1. **Own records only, and a stranger's id is a `404`.** No shape here carries a
 *    patient id, because there is only ever one: the session's. An appointment id that
 *    belongs to somebody else is answered as though it did not exist - distinguishing
 *    the two would tell a caller whether a stranger has an appointment at this clinic
 *    (`API.md` §1.6).
 * 2. **Instants, never wall clock.** Every time is a UTC instant plus the zone it should
 *    be read in. "10:00 AM" never travels: a patient in New York and a clinic in Los
 *    Angeles disagree about what it means.
 * 3. **The server decides; the shape explains.** `PortalPolicy` says what the API will
 *    *currently* allow and why, so a screen can show the right door and the right
 *    sentence. It is never the enforcement: `POST …/reschedule` and `POST …/cancel`
 *    re-check and answer `422` whatever was rendered (rules §17.2, §22.2).
 *
 * ## The join link is not in the payload until it is usable
 *
 * `PortalJoinWindow.url` is `null` outside the window the tenant configured. A
 * telehealth room address is a credential: anybody holding it can walk into the visit.
 * Keeping it out of the response until the window opens keeps it out of the bundle, out
 * of a screenshot and out of the DOM of a page somebody left open yesterday
 * (`SECURITY.md` §8, FR-PORTAL-02).
 */
/* ----------------------------------------------------------------- settings ---- */
/**
 * `tenants.settings -> 'portal'` - the handful of numbers the portal's behaviour turns
 * on (M10.1).
 *
 * A json block rather than columns, for the reason `SchedulingSettingsService` gives:
 * these are settings a clinic changes and the platform never queries across, so a column
 * each would be a migration apiece for no gain. Reads merge the defaults underneath, so
 * a tenant seeded before this milestone answers with a complete object.
 *
 * There is no admin endpoint for them yet (M11.12 owns the settings surface); the
 * defaults are `API.md` §2.4's "±60 min of start" and ADR-023's offer window, written
 * down once so that when the screen arrives it has a schema to bind to.
 */
export const PortalSettingsSchema = z
    .object({
    /** How long before the start the telehealth link appears. */
    joinWindowBeforeMinutes: z.int().min(0).max(1440).default(60),
    /** How long after it the link keeps working. */
    joinWindowAfterMinutes: z.int().min(0).max(1440).default(60),
    /**
     * Whether a patient rescheduling themselves keeps the clinician they have.
     *
     * Default false: continuity is a clinical preference, and a clinic that wants it
     * should say so rather than have the platform narrow every patient's options on
     * their behalf. FR-SCHED-05 already guarantees whoever is offered is licensed in
     * the patient's state and offers the visit type.
     */
    rescheduleKeepsProvider: z.boolean().default(false),
    /** How far ahead the reschedule picker looks. Bounded by the booking horizon too. */
    rescheduleHorizonDays: z.int().min(1).max(62).default(30),
    /**
     * May a patient cancel **inside** the window when the clinic charges for it?
     *
     * Default false, which is `API.md` §2.4's documented behaviour: inside the window the
     * cancel is refused `422` with the fee text and the clinic's phone, and a human
     * decides. A clinic that turns this on is saying the opposite - "let them cancel, and
     * charge them" - and the late-cancel intent is then raised by the same
     * `ChargeIntentsService.openFee` a staff member's button calls (FR-SCHED-10).
     *
     * It is a setting rather than a judgement because the two are genuinely different
     * businesses: a clinic that would rather keep the slot and phone the patient, and one
     * that would rather free the slot and take the fee. Reschedule is closed either way -
     * moving a visit four hours before it starts is a gap nobody can fill.
     */
    allowLateCancelWithFee: z.boolean().default(false),
    /** How long a waitlist offer is held before it lapses (ADR-023, FR-BOOK-16). */
    waitlistOfferMinutes: z.int().min(5).max(1440).default(60),
    /** How many entries one freed slot is offered to at once. */
    waitlistOfferFanout: z.int().min(1).max(20).default(3),
    /** How many offers one entry may be sent before it is left alone (rules §52.2). */
    waitlistMaxOffers: z.int().min(1).max(50).default(5),
})
    .strict();
export const PORTAL_SETTINGS_DEFAULTS = PortalSettingsSchema.parse({});
/* ------------------------------------------------------------------ profile ---- */
/**
 * The patient, as the patient may see themselves.
 *
 * Name and date of birth are present and **not** editable here: they are matched
 * against the EHR record and a patient who renames themselves in the portal becomes a
 * second person in Tebra. `API.md` §2.4 routes those changes through staff.
 */
export const PortalPatientSchema = z
    .object({
    id: z.uuid(),
    firstName: z.string().max(120),
    lastName: z.string().max(120),
    /** Convenience for a greeting; the two parts are authoritative. */
    fullName: z.string().max(241),
    /** The sign-in identity. Changing it is an auth flow, not a profile edit. */
    email: z.email(),
    phone: z.string().max(40).nullable().default(null),
    /** `YYYY-MM-DD`. Shown so the patient can check the clinic has the right person. */
    dateOfBirth: z.iso.date(),
    /** Two-letter state of residence; it decides which clinicians may see them. */
    state: z.string().length(2),
    timeZone: z.string().max(60),
    preferredLanguage: z.string().max(20).nullable().default(null),
})
    .strict();
/** One contact point, with why it may not be usable. */
export const PortalContactSchema = z
    .object({
    kind: z.enum(['email', 'sms', 'phone']),
    value: z.string().max(320),
    isPrimary: z.boolean().default(false),
    verified: z.boolean().default(false),
    /** A bounce or a wrong number. Staff can correct this one. */
    undeliverable: z.boolean().default(false),
    /** A STOP reply. Staff must not undo it (TCPA); only the patient can opt back in. */
    optedOut: z.boolean().default(false),
})
    .strict();
/**
 * What the clinic holds on file, for the readiness checklist (FR-PORTAL-02).
 *
 * Last four of the member id and nothing else. The full member id is the number a
 * fraudster needs to impersonate somebody to a payer, and a patient reading their own
 * portal does not need us to read it back to them.
 */
export const PortalInsuranceSchema = z
    .object({
    payerName: z.string().max(200),
    memberIdLast4: z
        .string()
        .regex(/^[A-Za-z0-9]{1,4}$/)
        .nullable()
        .default(null),
    status: z.enum(['verified', 'pending', 'self_pay', 'needs_review']),
    /** When the payer last answered. Null while nothing has been checked. */
    checkedAt: z.iso.datetime().nullable().default(null),
})
    .strict();
/**
 * One consent, with the wording the patient actually saw.
 *
 * `ConsentRecord` says *that* they agreed and to which version; `text` is what that
 * version says, read back from the tenant's own settings. A consent whose text cannot
 * still be produced is not a consent anybody can defend two years later, so the portal
 * shows both or says plainly that the clinic has not published the wording.
 */
export const PortalConsentSchema = ConsentRecordSchema.extend({
    /** Null when the tenant has not written this one; never a platform default. */
    text: z.string().nullable().default(null),
}).strict();
/** `GET /patient/me` (`API.md` §2.4). */
export const PortalMeResponseSchema = z
    .object({
    data: z
        .object({
        patient: PortalPatientSchema,
        contacts: z.array(PortalContactSchema),
        preferences: NotificationPreferencesSchema,
        /** Null for a self-pay patient and for one who has given the clinic nothing. */
        insurance: PortalInsuranceSchema.nullable().default(null),
        consents: z.array(PortalConsentSchema),
    })
        .strict(),
})
    .strict();
/**
 * `PATCH /patient/me` - the three fields a patient owns (`API.md` §2.4).
 *
 * An absent key means "leave it"; an explicit `null` on `phone` means "remove it". The
 * distinction matters: a patient deleting a wrong number is a different instruction
 * from a form that did not include the field.
 */
export const UpdatePortalContactRequestSchema = z
    .object({
    phone: z
        .string()
        .trim()
        .min(7)
        .max(40)
        .nullable()
        .optional()
        .transform((value) => (value === '' ? null : value)),
    timeZone: z.string().min(1).max(60).optional(),
    preferredLanguage: z.string().min(2).max(20).nullable().optional(),
})
    .strict();
export const PortalPatientResponseSchema = z
    .object({ data: z.object({ patient: PortalPatientSchema }).strict() })
    .strict();
/* ------------------------------------------------------------- appointments ---- */
/**
 * The status vocabulary a *patient* is shown.
 *
 * Narrower than `appointments.status`: `pending_ehr` and `needs_attention` are both
 * "we have your booking and we are sorting it out" from the outside, and telling a
 * patient that their appointment failed to reach the clinic's calendar invites a
 * duplicate booking. Staff see the real status on the admin surface.
 */
export const PORTAL_APPOINTMENT_STATUSES = [
    'scheduled',
    'confirmed',
    'checked_in',
    'completed',
    'cancelled',
    'no_show',
];
export const PortalAppointmentStatusSchema = z.enum(PORTAL_APPOINTMENT_STATUSES);
/** How far the patient has got with the paperwork for one visit (FR-INTAKE-03). */
export const PORTAL_INTAKE_STATUSES = [
    'not_required',
    'not_started',
    'in_progress',
    'submitted',
];
export const PortalIntakeStatusSchema = z.enum(PORTAL_INTAKE_STATUSES);
export const PortalAppointmentSummarySchema = z
    .object({
    id: z.uuid(),
    /** `BK-…`. What a patient reads out on the phone. */
    reference: z.string().max(20),
    startAt: z.iso.datetime(),
    endAt: z.iso.datetime(),
    status: PortalAppointmentStatusSchema,
    /** "New patient consultation". A visit type, never a reason or a diagnosis. */
    typeName: z.string().max(200),
    durationMinutes: z.int().positive(),
    /** Null while the clinic has not published a name for the clinician. */
    providerName: z.string().max(200).nullable().default(null),
    clinicTimeZone: z.string().max(60),
    intakeStatus: PortalIntakeStatusSchema,
})
    .strict();
/**
 * When the telehealth room can be entered, and its address once it can.
 *
 * `url` is null outside `[opensAt, closesAt]`, whatever the client asks for.
 */
export const PortalJoinWindowSchema = z
    .object({
    url: z.url().nullable().default(null),
    opensAt: z.iso.datetime(),
    closesAt: z.iso.datetime(),
    /** Plain sentences the screen prints verbatim. Never clinical instructions. */
    instructions: z.array(z.string().max(300)).default([]),
})
    .strict();
/**
 * The tenant's reschedule/cancel rule as the API currently applies it to **this** visit
 * (ADR-022, FR-PORTAL-03).
 *
 * `reason` is the server's own sentence and is shown as written, so the portal never
 * invents a second vocabulary for the same refusal.
 */
export const PortalPolicySchema = z
    .object({
    windowHours: z.int().min(0),
    /**
     * Is this visit inside that window right now?
     *
     * Separate from `canCancel` because a clinic may permit a late cancel *and* charge
     * for it (`portal.allowLateCancelWithFee`): the screen then needs to warn about the
     * fee before it opens the same door it would have opened for free yesterday.
     */
    insideWindow: z.boolean().default(false),
    canReschedule: z.boolean(),
    canCancel: z.boolean(),
    /** Why not, when either is false. Null when both are true. */
    reason: z.string().max(400).nullable().default(null),
    /** Machine-readable twin of `reason`, for the handful of branches a screen takes. */
    reasonCode: z
        .enum([
        'policy_window_closed',
        'reschedule_limit',
        'not_active',
        'self_service_disabled',
        'in_the_past',
    ])
        .nullable()
        .default(null),
    /** How many self-service moves are left. Null when the tenant does not cap them. */
    reschedulesRemaining: z.int().min(0).nullable().default(null),
    /** The late-cancel fee the clinic may apply inside the window. Null means none. */
    lateFeeCents: z.int().min(0).nullable().default(null),
    currency: z.string().length(3).default('USD'),
    /** What to dial when self-service is closed. Null when the clinic published none. */
    clinicPhone: z.string().max(40).nullable().default(null),
    /** The clinic's own policy wording, when it has written one. */
    patientText: z.string().max(2000).nullable().default(null),
})
    .strict();
export const PortalPriceQuoteSchema = z
    .object({
    kind: z.enum(['insurance', 'self_pay', 'pending']),
    amountCents: z.int().min(0).nullable().default(null),
    currency: z.string().length(3).default('USD'),
    /** One plain sentence. The server writes it; the screen prints it. */
    explanation: z.string().max(400),
})
    .strict();
export const PortalAppointmentDetailSchema = PortalAppointmentSummarySchema.extend({
    join: PortalJoinWindowSchema,
    policy: PortalPolicySchema,
    priceQuote: PortalPriceQuoteSchema.nullable().default(null),
    /** The same three-legged indicator staff see (`intake`, `insurance`, `card`). */
    readiness: ReadinessSchema,
    cancelledAt: z.iso.datetime().nullable().default(null),
    cancellationReason: z.string().max(400).nullable().default(null),
}).strict();
export const PortalAppointmentListResponseSchema = z
    .object({
    data: z
        .object({
        appointments: z.array(PortalAppointmentSummarySchema),
        nextCursor: z.string().max(200).nullable().default(null),
    })
        .strict(),
})
    .strict();
export const PortalAppointmentResponseSchema = z
    .object({ data: z.object({ appointment: PortalAppointmentDetailSchema }).strict() })
    .strict();
/* -------------------------------------------------------------- rescheduling ---- */
/** One selectable day. `date` is the **clinic's** calendar date. */
export const PortalRescheduleDaySchema = z
    .object({
    date: z.iso.date(),
    slotCount: z.int().min(0),
})
    .strict();
export const PortalRescheduleSlotSchema = z
    .object({ startAt: z.iso.datetime(), endAt: z.iso.datetime() })
    .strict();
/**
 * `GET /patient/appointments/:id/reschedule-options`.
 *
 * `allowed: false` is a complete answer: `reason` says why and the screen shows the
 * clinic's phone instead of a grid.
 *
 * `days` is always the range's summary; `slots` is filled in **only** when the caller
 * named a `?date=`. One endpoint answers both the funnel-style date strip and the grid
 * under it, and a client that wants the whole range pages through the days rather than
 * asking for a month of slot lists it will not render.
 */
export const PortalRescheduleOptionsSchema = z
    .object({
    allowed: z.boolean(),
    reason: z.string().max(400).nullable().default(null),
    reasonCode: PortalPolicySchema.shape.reasonCode,
    feeCents: z.int().min(0).nullable().default(null),
    /**
     * The currency `feeCents` is in - the same field `PortalPolicy` and
     * `PortalPriceQuote` carry, and always the same value as the policy's, because both
     * come from the one `cancellation_policies` row this visit is judged by. Without it
     * a screen formatting the late-cancel warning had to assume dollars.
     */
    currency: z.string().length(3).default('USD'),
    clinicPhone: z.string().max(40).nullable().default(null),
    clinicTimeZone: z.string().max(60),
    /** The first and last clinic dates the range covers. */
    from: z.iso.date(),
    to: z.iso.date(),
    days: z.array(PortalRescheduleDaySchema),
    /** The day the caller asked for, when they asked for one. */
    date: z.iso.date().nullable().default(null),
    slots: z.array(PortalRescheduleSlotSchema).default([]),
    /**
     * Whether the options are limited to the clinician already booked.
     *
     * `scheduling.rescheduleKeepsProvider` in the tenant's settings. False means any
     * clinician licensed in the patient's state who offers the visit type.
     */
    sameProviderOnly: z.boolean().default(false),
})
    .strict();
export const PortalRescheduleOptionsResponseSchema = z
    .object({ data: PortalRescheduleOptionsSchema })
    .strict();
export const PortalRescheduleRequestSchema = z
    .object({ startAt: z.iso.datetime({ offset: true }) })
    .strict();
/**
 * `POST /patient/appointments/:id/cancel`.
 *
 * `reason` is optional and free text, which makes it the one field on this surface a
 * patient could type a clinical fact into. It is stored on the appointment like a staff
 * cancellation reason and never travels to a vendor, an email or a log line.
 */
export const PortalCancelRequestSchema = z
    .object({ reason: z.string().trim().max(400).optional() })
    .strict();
/**
 * What a cancellation did, beyond changing the status.
 *
 * `feeCents` is what the clinic *intends* to charge, not what has been taken: the
 * charge intent is raised here and the money moves later, or never, depending on the
 * clinic. Saying so in the response is what lets the confirmation screen be honest.
 */
export const PortalCancelResponseSchema = z
    .object({
    data: z
        .object({
        appointment: PortalAppointmentDetailSchema,
        feeApplied: z.boolean().default(false),
        feeCents: z.int().min(0).default(0),
        currency: z.string().length(3).default('USD'),
    })
        .strict(),
})
    .strict();
/* -------------------------------------------------------------- secure link ---- */
/**
 * `POST /auth/secure-link/consume` (M10.2, `SECURITY.md` §4).
 *
 * The token is a credential, so it travels in a body rather than a query string: a URL
 * ends up in the browser history, the referrer and every proxy log between here and the
 * patient. The emailed link lands on a portal page, which reads the token out of the
 * fragment and posts it here exactly once.
 */
export const SecureLinkConsumeRequestSchema = z
    .object({ token: z.string().min(20).max(512) })
    .strict();
export const SecureLinkConsumeResponseSchema = z
    .object({
    data: z
        .object({
        /** Where the portal should send the patient. Always a same-origin path. */
        redirectPath: z.string().max(300),
        expiresAt: z.iso.datetime(),
    })
        .strict(),
})
    .strict();
/** What a minted link is *for*. Decides the landing path and nothing else. */
export const SECURE_LINK_TARGETS = [
    'portal',
    'appointment',
    'reschedule',
    'intake',
    'waitlist',
];
export const SecureLinkTargetSchema = z.enum(SECURE_LINK_TARGETS);
//# sourceMappingURL=portal.js.map